import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  ImChannelProactivityExperimentCohortUser,
  ProactivenessCohortChannelFilter,
  ProactivenessSectionCohort,
  ProactivenessSection,
  RoutineDeliveryChannel,
  SendImChannelProactivityExperimentNowResult,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';

const CHANNEL_LABELS: Record<RoutineDeliveryChannel, string> = {
  imessage: 'iMessage',
  whatsapp_business: 'WhatsApp',
};

type CohortTableUser = ImChannelProactivityExperimentCohortUser & {
  flag_keys?: string[];
};

type SendResult = Omit<
  SendImChannelProactivityExperimentNowResult,
  'scheduled_for'
> & {
  scheduled_for: string | null;
  scheduled_local_at?: string | null;
};

const getSendResultTitle = (result: SendResult) => {
  if (result.queued_users.length === 0) return 'No sends were confirmed.';
  if (result.scheduled_local_at) {
    return `Scheduled for ${result.scheduled_local_at.replace('T', ' ')} in each recipient's timezone`;
  }
  if (!result.scheduled_for) return 'Queued';
  const scheduledTime = new Date(result.scheduled_for).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Queued for ${scheduledTime}`;
};

const columnHelper = createColumnHelper<CohortTableUser>();
const USER_COLUMNS = [
  columnHelper.accessor('email', { header: 'Email' }),
  columnHelper.accessor('delivery_channel', {
    header: 'Channel',
    cell: ({ getValue }) => {
      const channel = getValue();
      return channel ? CHANNEL_LABELS[channel] : 'No channel';
    },
  }),
];
const COHORT_USER_COLUMNS = [
  ...USER_COLUMNS,
  columnHelper.accessor('flag_keys', {
    header: 'Flags',
    cell: ({ getValue }) => getValue()?.join(', ') ?? 'None',
  }),
];

type ExperimentNoticesProps = {
  rolloutsError: Error | null;
  cohorts?: ProactivenessSectionCohort[];
  cohortError: Error | null;
  syncError: Error | null;
  sendResult?: SendResult;
  sendError: Error | null;
  pauseError: Error | null;
  isCohortLoading: boolean;
  cohortChannelFilters: Record<
    ProactivenessSection,
    ProactivenessCohortChannelFilter
  >;
  cohortFlagFilters: Record<ProactivenessSection, string>;
  onCohortPageChange: (section: ProactivenessSection, page: number) => void;
  onCohortChannelFilterChange: (
    section: ProactivenessSection,
    channel: ProactivenessCohortChannelFilter
  ) => void;
  onCohortFlagFilterChange: (
    section: ProactivenessSection,
    flagKey: string
  ) => void;
  cohortDescription?: string;
  sendFailureMessage?: string;
};

type UserPagination = {
  page: number;
  totalCount: number;
  limit: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
};

const CohortUsers = ({
  users,
  pagination,
  showFlags = false,
}: {
  users: CohortTableUser[];
  pagination?: UserPagination;
  showFlags?: boolean;
}) => {
  const table = useReactTable({
    data: users,
    columns: showFlags ? COHORT_USER_COLUMNS : USER_COLUMNS,
    getRowId: (user) => user.email,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: pagination ? undefined : getPaginationRowModel(),
    manualPagination: Boolean(pagination),
    rowCount: pagination?.totalCount,
    ...(pagination && {
      state: {
        pagination: {
          pageIndex: pagination.page - 1,
          pageSize: pagination.limit,
        },
      },
    }),
    initialState: { pagination: { pageSize: 10 } },
  });
  const page = pagination?.page ?? table.getState().pagination.pageIndex + 1;
  const pageCount = pagination
    ? Math.max(1, Math.ceil(pagination.totalCount / pagination.limit))
    : table.getPageCount();
  const totalCount = pagination?.totalCount ?? users.length;

  if (users.length === 0) {
    return <p className="mt-2 text-muted-foreground">No users.</p>;
  }

  return (
    <>
      <Table containerClassName="mt-2 rounded-md border">
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => (
                <TableHead key={header.id} className="h-9 px-3">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="px-3 py-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <nav
        aria-label="User pagination"
        className="mt-2 flex flex-wrap items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground" aria-live="polite">
            Page {page} of {pageCount}, {totalCount} users
          </p>
          {pagination?.isLoading && (
            <div
              role="status"
              aria-label="Loading users"
              className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              pagination
                ? pagination.onPageChange(page - 1)
                : table.previousPage()
            }
            disabled={page === 1 || pagination?.isLoading}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              pagination ? pagination.onPageChange(page + 1) : table.nextPage()
            }
            disabled={page === pageCount || pagination?.isLoading}
          >
            Next
          </Button>
        </div>
      </nav>
    </>
  );
};

/** Shows the current cohort mapping and recent action results. */
export const ExperimentNotices = ({
  rolloutsError,
  cohorts,
  cohortError,
  syncError,
  sendResult,
  sendError,
  pauseError,
  isCohortLoading,
  cohortChannelFilters,
  cohortFlagFilters,
  onCohortPageChange,
  onCohortChannelFilterChange,
  onCohortFlagFilterChange,
  cohortDescription = "Users appear when at least one proactive routine's PostHog flag is enabled. The Flags column shows every match. Channels reflect existing assignments. A routine with listed integrations needs at least one connected before it is assigned.",
  sendFailureMessage = 'Queueing was not confirmed for these users. Some may still receive the message. Sending to the whole cohort again can create duplicates.',
}: ExperimentNoticesProps) => (
  <>
    {rolloutsError && (
      <p className="mb-4 rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {rolloutsError.message}
      </p>
    )}
    {cohorts?.map(
      ({ section, users, available_flag_keys, total_count, page, limit }) => (
        <section
          key={section}
          aria-label={
            section === 'production'
              ? 'Production cohort'
              : 'Experiments cohort'
          }
          className="mb-4 rounded-md border px-4 py-3 text-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium">
              {section === 'production' ? 'Production' : 'Experiments'}: enabled
              PostHog users
            </p>
            <div className="flex flex-wrap gap-2">
              <Select
                aria-label={`Filter ${
                  section === 'production' ? 'Production' : 'Experiments'
                } cohort by flag`}
                className="h-8 w-auto max-w-64 py-1"
                value={cohortFlagFilters[section]}
                disabled={isCohortLoading}
                onChange={(event) =>
                  onCohortFlagFilterChange(section, event.target.value)
                }
              >
                <option value="">All flags</option>
                {available_flag_keys.map((flagKey) => (
                  <option key={flagKey} value={flagKey}>
                    {flagKey}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={`Filter ${
                  section === 'production' ? 'Production' : 'Experiments'
                } cohort by channel`}
                className="h-8 w-auto py-1"
                value={cohortChannelFilters[section]}
                disabled={isCohortLoading}
                onChange={(event) =>
                  onCohortChannelFilterChange(
                    section,
                    event.target.value as ProactivenessCohortChannelFilter
                  )
                }
              >
                <option value="all">All channels</option>
                <option value="whatsapp_business">WhatsApp</option>
                <option value="imessage">iMessage</option>
                <option value="none">No channel</option>
              </Select>
            </div>
          </div>
          <p className="mt-1 text-muted-foreground">{cohortDescription}</p>
          <CohortUsers
            users={users}
            showFlags
            pagination={{
              page,
              totalCount: total_count,
              limit,
              isLoading: isCohortLoading,
              onPageChange: (nextPage) => onCohortPageChange(section, nextPage),
            }}
          />
        </section>
      )
    )}
    {cohortError && (
      <p className="mb-4 rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {cohortError.message}
      </p>
    )}
    {syncError && (
      <p className="mb-4 rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {syncError.message}
      </p>
    )}
    {sendResult && (
      <section className="mb-4 rounded-md border px-4 py-3 text-sm">
        <p className="font-medium">{getSendResultTitle(sendResult)}</p>
        {sendResult.queued_users.length > 0 && (
          <CohortUsers users={sendResult.queued_users} />
        )}
        {sendResult.failed_users && sendResult.failed_users.length > 0 && (
          <div role="alert" className="mt-3">
            <p className="text-destructive">{sendFailureMessage}</p>
            <CohortUsers users={sendResult.failed_users} />
          </div>
        )}
      </section>
    )}
    {sendError && (
      <p className="mb-4 rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {sendError.message}
      </p>
    )}
    {pauseError && (
      <p className="mb-4 rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {pauseError.message}
      </p>
    )}
  </>
);
